// The site guide's conversation, as pure functions so it can be tested without a browser.
// Each step returns {state, say, options, navigate, highlight, chat}:
//   say       what the guide says (shown and, when voice is on, spoken)
//   options   quick replies [{id, label}]
//   navigate  a path to open; highlight a CSS selector to point at
//   chat      free text to send to the assistant; `say` is then asked again after its answer

import { PUBLIC_NAV_LINKS } from '../constants/publicNav';

// window event: detail {kind: 'tour'} or {kind: 'tutorial', title, steps}
export const GUIDE_WALK_EVENT = 'enterweu:guide-walk';

export const startGuideWalk = (detail) => {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(GUIDE_WALK_EVENT, { detail }));
};

export const USER_TYPE_CHOICES = [
  { id: 'candidate', label: 'Candidate or student', words: /candidate|student|job ?seeker|learner|fresher|graduate|looking for (a )?job/ },
  { id: 'consultancy', label: 'Consultancy or recruiter', words: /consult|recruit|agency|staffing|\bhr\b|hiring|placement agency/ },
  { id: 'institution', label: 'College or institution', words: /institut|college|university|school|campus|\btpo\b|training (and|&) placement/ },
  { id: 'partner', label: 'EdTech partner', words: /partner|edtech|course|publisher|training provider/ },
  { id: 'visitor', label: 'Just exploring', words: /explor|visit|look(ing)? around|brows|just|not sure|guest|curious/ },
];

const TYPE_LABEL = Object.fromEntries(USER_TYPE_CHOICES.map((c) => [c.id, c.label.toLowerCase()]));

export const PUBLIC_PAGE_HINTS = {
  '/': 'The home page gives the big picture: seven interview modes, AI interviews, and talent discovery.',
  '/features': 'Features explains each interview mode, AI analysis by WeSenseU, and what reports contain.',
  '/pricing': 'Pricing lists the plans for candidates, consultancies and institutions.',
  '/partners': 'Partners shows approved EdTech publishers whose tutorials we suggest for weak topics.',
  '/tutorials': 'Tutorials has step-by-step guides for every account type. I can walk you through any of them.',
  '/about': 'About tells you who we are and how we treat your data.',
  '/help-support': 'Help and Support is where you can ask questions or raise a ticket.',
  '/login': 'This is the login page. Enter your username and password to sign in.',
};

export const SLUG_HINTS = {
  dashboard: 'Your dashboard summarises scores, upcoming rounds and what to do next.',
  invites: 'Invites is where you accept firm management requests, college memberships, team invites, and report-share consent.',
  openings: 'Openings lists every client position so you can open its interview pipeline without drilling into a company first.',
  'bulk-operations': 'Bulk invite lets you add many candidates by roster paste or resume upload.',
  'candidate-reports': 'Interview reports lists plans this firm sponsored and opens each plan or client PDF.',
  'interview-history': 'Interviews and reports lists your plans and practice reports in one place.',
  'mock-interviews': 'Mock Interviews lets you practise full rounds against a role.',
  'interview-planner': 'Plan from a JD builds an interview plan from a job description and your resume.',
  'review-answers': 'Review answers lets you vote on the best answers for skills you have proven.',
  'interview-modes': 'Interview Modes covers voice, video, written, MCQ, aptitude, coding and typing.',
  'job-descriptions': 'Job Descriptions stores the roles you are targeting.',
  subscriptions: 'Here you can see and change your plan.',
  tutorials: 'Tutorials has step-by-step guides; I can walk you through any of them.',
  support: 'Support is where you ask for help or raise a ticket.',
  'crm-pipeline': 'The CRM pipeline tracks every candidate from sourcing to placement.',
  'candidate-management': 'Candidates is your list of people you represent.',
  'consultancy-team': 'CRM Team manages your recruiters and their roles.',
  'talent-discovery': 'Talent Discovery searches discoverable candidates by city and role family.',
  'client-companies': 'Client Companies holds the employers you hire for, and their openings.',
  'interview-scheduling': 'Interview Scheduling assigns AI-interview plans to candidates.',
  'business-intelligence': 'Business Intelligence shows placement and revenue trends.',
  'consultancy-ops': 'Ops covers employee management and GST invoicing.',
  'placement-analytics': 'CRM Analytics breaks down your funnel and recruiter performance.',
  'subscription-billing': 'Subscription and Billing manages your plan and invoices.',
  'cohort-learners': 'Students lists people enrolled at your institution.',
  curriculum: 'Curriculum is where an enrolled student records years, semesters, subjects, and a subject test.',
  'institution-curriculum': 'Curriculum shows each enrolled student’s years, semesters, subjects, and subject-test results.',
  'institution-profile': 'Institution profile is what recruiters see about your college.',
  'institution-plans': 'Interview plans lets you send practice interviews to students.',
  'placement-drives': 'Placement drives runs campus hiring with interview rounds.',
  'institution-trainings': 'Training requests asks EnterWeU for an in-person or online course. Accepted requests show the plan and recordings.',
  'my-trainings': 'My trainings lists courses your institution enrolled you in, including recordings and trainings advertised to you.',
  'admin-institution-trainings': 'Institution training is where a superadmin accepts requests, writes the response, and attaches recordings.',
  'admin-training-advertise': 'Advertise training sends one course to selected institutions and students. Anyone not selected does not see it.',
  'partner-team': 'Staff team is where a partner owner invites a content editor.',
  'institution-interviews': 'Training Interviews shows practice interviews your students have taken.',
  'training-reports': 'Reports and Evaluations collects every student report.',
  'partner-dashboard': 'Your partner catalog is where you list tutorials for review.',
  partners: 'The public partners page shows what candidates see about you.',
  profile: 'Profile holds your details and discoverability settings.',
  settings: 'Settings holds your account, notifications and password.',
};

export const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9&\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const matchYesNo = (text) => {
  const t = normalize(text);
  if (!t) return null;
  if (/\b(repeat|again|pardon|say that|come again)\b/.test(t)) return 'repeat';
  if (/^(no|nope|nah|stop|exit|cancel|not now|later|quit|end|enough|skip (the )?tour)\b/.test(t)) return 'no';
  if (t.split(' ').length > 4) return null;
  if (/^(yes|yeah|yep|yup|sure|ok|okay|continue|next|go on|go ahead|proceed|alright|carry on|let s go)\b/.test(t)) return 'yes';
  return null;
};

export const matchUserType = (text) => {
  const t = normalize(text);
  if (!t) return null;
  const hit = USER_TYPE_CHOICES.find((choice) => choice.id === t || choice.words.test(t));
  return hit ? hit.id : null;
};

// A question for the assistant rather than a page to open ("what is talent discovery?").
export const isQuestion = (text) => {
  const t = normalize(text);
  if (/\b(take me|go to|open|show me|navigate|bring me)\b/.test(t)) return false;
  return /\?\s*$/.test(String(text || '')) || /^(what|how|why|when|who|can|could|does|do|is|are|should|which|tell me)\b/.test(t);
};

// Best menu item for a spoken or typed page name.
export const matchTab = (text, items) => {
  const t = normalize(text)
    .replace(/^(can you |could you |please )?(go to|open|take me to|show( me)?|navigate to|visit|bring me to)\s+(the\s+)?/, '')
    .replace(/\s*(page|tab|section)$/, '');
  if (t.length < 3) return null;
  let best = null;
  let bestScore = 0;
  (items || []).forEach((item) => {
    const title = normalize(item.title);
    let score = 0;
    if (title === t) score = 3;
    else if (t.includes(title) || title.includes(t)) score = 2;
    else {
      const words = t.split(' ').filter((w) => w.length > 3);
      const shared = words.filter((w) => title.split(' ').some((x) => x.startsWith(w) || w.startsWith(x)));
      if (shared.length) score = shared.length / Math.max(words.length, 1);
    }
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  });
  return bestScore >= 0.5 ? best : null;
};

const option = (id, label) => ({ id, label });
const YES_NO_STEP = [option('yes', 'Yes, continue'), option('repeat', 'Repeat'), option('no', 'Stop')];

const publicItems = () =>
  PUBLIC_NAV_LINKS.filter((link) => !link.external && PUBLIC_PAGE_HINTS[link.to]).map((link) => ({
    title: link.label,
    path: link.to,
  }));

export const publicTourSteps = () =>
  publicItems().map((item) => ({
    title: item.title,
    about: PUBLIC_PAGE_HINTS[item.path],
    path: item.path,
    highlight: `a[href="${item.path}"]`,
  }));

export const menuTourSteps = (menu) =>
  (menu || []).map((item) => ({
    title: item.title,
    about: SLUG_HINTS[item.slug] || `${item.title} is in your menu.`,
    path: item.path,
    highlight: `a[href="${item.path}"]`,
  }));

// Steps of a tutorial card on the Tutorials page.
export const tutorialSteps = (guide) =>
  (guide?.steps || []).map((step, index) => ({
    title: step.title,
    about: step.body,
    path: null,
    link: step.href || null,
    highlight: `[data-tutorial-step="${index}"]`,
  }));

const tabList = (menu) => {
  const names = (menu || []).map((item) => item.title);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const menuOptions = (state) => [
  ...(state.menu || []).slice(0, 6).map((item) => option(`tab:${item.path}`, item.title)),
  option('tour', state.loggedIn ? 'Guided tour of my menu' : 'Website tour'),
];

const result = (state, say, extra = {}) => ({
  state,
  say,
  options: [],
  navigate: null,
  highlight: null,
  chat: null,
  ...extra,
});

export const startGuide = ({ user = null, menu = [] } = {}) => {
  if (user) {
    const state = { node: 'menu', loggedIn: true, name: user.first_name || user.username || '', menu, walk: null };
    const name = state.name ? `, ${state.name}` : '';
    return result(
      state,
      `Welcome back${name}! I'm your EnterWeU guide. Your menu has ${tabList(menu)}. ` +
        'Tell me a tab name and I will take you there, or say "tour" and I will walk you through each one.',
      { options: menuOptions(state) },
    );
  }
  const state = { node: 'ask_type', loggedIn: false, userType: null, menu: publicItems(), walk: null };
  return result(
    state,
    "Welcome to EnterWeU! I'm your guide. Are you a candidate or student, a consultancy, a college or institution, " +
      'or an EdTech partner? Or are you just exploring?',
    { options: USER_TYPE_CHOICES.map((c) => option(`type:${c.id}`, c.label)) },
  );
};

const askAccount = (state) => {
  const label = TYPE_LABEL[state.userType] || 'member';
  return result(
    { ...state, node: 'ask_account' },
    `Great, a ${label}. Do you already have an account? I can take you to log in, help you create an account, ` +
      'or show you around the website first.',
    {
      options: [option('login', 'Log in'), option('register', 'Create an account'), option('tour', 'Show me the website')],
    },
  );
};

const walkStep = (state, prefix = '') => {
  const { steps, i, kind } = state.walk;
  const step = steps[i];
  const next = steps[i + 1];
  const count = kind === 'tutorial' ? `Step ${i + 1} of ${steps.length}: ` : '';
  const ask = next
    ? kind === 'tutorial'
      ? ' Ready for the next step?'
      : ` Shall we continue to ${next.title}?`
    : kind === 'tutorial'
      ? ' That was the last step. Shall I finish here?'
      : ' That was the last stop. Shall I finish the tour?';
  const options = step.link ? [YES_NO_STEP[0], option('open', 'Open this page'), ...YES_NO_STEP.slice(1)] : YES_NO_STEP;
  return result(state, `${prefix}${count}${step.title}. ${step.about}${ask}`, {
    options,
    navigate: step.path || null,
    highlight: step.highlight || null,
  });
};

export const startWalk = (state, steps, kind = 'tour', intro = '') => {
  if (!steps || !steps.length) return result(state, 'There is nothing to walk through here yet.');
  return walkStep({ ...state, node: 'walk', walk: { steps, i: 0, kind } }, intro);
};

const endWalk = (state, say) => {
  if (!state.loggedIn && state.userType && state.userType !== 'visitor') {
    return result({ ...state, node: 'ask_account', walk: null }, `${say} Would you like to log in or create an account?`, {
      options: [option('login', 'Log in'), option('register', 'Create an account'), option('menu', 'Keep browsing')],
    });
  }
  const back = { ...state, node: 'menu', walk: null };
  return result(back, `${say} Tell me a page name whenever you want to go somewhere, or ask me anything.`, {
    options: menuOptions(back),
  });
};

const goToTab = (state, item) =>
  result(state, `Opening ${item.title}. ${SLUG_HINTS[item.slug] || PUBLIC_PAGE_HINTS[item.path] || ''}`.trim(), {
    navigate: item.path,
    highlight: `a[href="${item.path}"]`,
    options: menuOptions(state),
  });

const tourFor = (state) =>
  startWalk(state, state.loggedIn ? menuTourSteps(state.menu) : publicTourSteps(), 'tour', "Let's start. ");

// optionId comes from a quick-reply button; text from typing or speech.
export const guideReply = (state, text, optionId = null) => {
  const said = String(text || '').trim();
  const yesNo = optionId && ['yes', 'no', 'repeat'].includes(optionId) ? optionId : matchYesNo(said);

  if (optionId && optionId.startsWith('tab:')) {
    const item = (state.menu || []).find((m) => `tab:${m.path}` === optionId);
    if (item) return goToTab({ ...state, node: 'menu', walk: null }, item);
  }

  switch (state.node) {
    case 'ask_type': {
      const type = optionId && optionId.startsWith('type:') ? optionId.slice(5) : matchUserType(said);
      if (!type) {
        return result(state, 'Sorry, I did not catch that. Which of these describes you best?', {
          options: USER_TYPE_CHOICES.map((c) => option(`type:${c.id}`, c.label)),
        });
      }
      const next = { ...state, userType: type };
      if (type === 'visitor') {
        return result({ ...next, node: 'ask_tour' }, 'No problem. Would you like a quick tour of the website?', {
          options: [option('yes', 'Yes, show me around'), option('no', 'No, thanks')],
        });
      }
      return askAccount(next);
    }
    case 'ask_account': {
      const t = normalize(said);
      const choice =
        optionId ||
        (/\b(log ?in|sign ?in|have an account|already)\b/.test(t)
          ? 'login'
          : /\b(sign ?up|register|create|new account|join|don t have)\b/.test(t)
            ? 'register'
            : /\b(tour|show|website|around|explore|browse)\b/.test(t)
              ? 'tour'
              : null);
      if (choice === 'login') {
        return result(
          { ...state, node: 'menu' },
          'Taking you to the login page. Once you sign in, I will show you your menu.',
          { navigate: '/login', options: menuOptions(state) },
        );
      }
      if (choice === 'register') {
        const type = ['candidate', 'consultancy', 'institution', 'partner'].includes(state.userType)
          ? state.userType
          : 'candidate';
        return result(
          { ...state, node: 'menu' },
          `Here is the sign-up form for a ${TYPE_LABEL[type]} account. Fill it in and I will guide you from there.`,
          { navigate: `/register/${type}`, options: menuOptions(state) },
        );
      }
      if (choice === 'tour') return tourFor(state);
      if (choice === 'menu') return endWalk({ ...state, userType: 'visitor' }, 'Sure.');
      return result(state, 'Would you like to log in, create an account, or see the website first?', {
        options: [option('login', 'Log in'), option('register', 'Create an account'), option('tour', 'Show me the website')],
        chat: said || null,
      });
    }
    case 'ask_tour': {
      if (yesNo === 'yes') return tourFor(state);
      if (yesNo === 'no') return endWalk(state, 'Okay.');
      return { ...guideReply({ ...state, node: 'menu' }, said, optionId) };
    }
    case 'walk': {
      const walk = state.walk;
      if (optionId === 'open' && walk.steps[walk.i].link) {
        return result(state, `Opening ${walk.steps[walk.i].title}. Say "next" when you are ready to continue.`, {
          navigate: walk.steps[walk.i].link,
          options: YES_NO_STEP,
        });
      }
      const named = !optionId && !isQuestion(said) && said.split(/\s+/).length > 1 ? matchTab(said, state.menu) : null;
      if (named) return goToTab({ ...state, node: 'menu', walk: null }, named);
      if (yesNo === 'repeat') return walkStep(state);
      if (yesNo === 'no') return endWalk(state, walk.kind === 'tutorial' ? 'Okay, we can pick this up later.' : 'Okay, tour stopped.');
      if (yesNo === 'yes') {
        if (walk.i + 1 >= walk.steps.length) {
          return endWalk(state, walk.kind === 'tutorial' ? 'You have finished this guide.' : 'That is the tour.');
        }
        return walkStep({ ...state, walk: { ...walk, i: walk.i + 1 } });
      }
      const item = isQuestion(said) ? null : matchTab(said, state.menu);
      if (item) return goToTab({ ...state, node: 'menu', walk: null }, item);
      const step = walk.steps[walk.i + 1];
      const ask = step ? `Shall we continue to ${walk.kind === 'tutorial' ? 'the next step' : step.title}?` : 'Shall I finish here?';
      return result(state, ask, { options: YES_NO_STEP, chat: said || null });
    }
    default: {
      const t = normalize(said);
      if (optionId === 'tour' || /\b(tour|walk me through|show me around|guide me)\b/.test(t)) return tourFor(state);
      if (/\b(menu|tabs|options|what can i do|where can i go)\b/.test(t) || t === 'help') {
        return result(state, `You can go to ${tabList(state.menu)}. Which one?`, { options: menuOptions(state) });
      }
      const item = isQuestion(said) ? null : matchTab(said, state.menu);
      if (item) return goToTab(state, item);
      if (!state.loggedIn && /\b(log ?in|sign ?in)\b/.test(t)) {
        return result(state, 'Taking you to the login page.', { navigate: '/login', options: menuOptions(state) });
      }
      return result(state, '', { options: menuOptions(state), chat: said || null });
    }
  }
};

// One line about the page the user just opened, for guidance as they move around.
export const pageHint = (pathname, menu) => {
  const item = (menu || []).find((m) => m.path === pathname);
  if (item) return SLUG_HINTS[item.slug] ? `You are on ${item.title}. ${SLUG_HINTS[item.slug]}` : null;
  return PUBLIC_PAGE_HINTS[pathname] || null;
};
